import { DataClientError, type DataClientErrorCode, type TokenProvider } from '@kk/domain';

/** The subset of WHATWG fetch the client uses; satisfied by React Native / Node `fetch` and by test adapters. */
export interface FetchResponseLike {
  status: number;
  text(): Promise<string>;
}
export type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body?: string; signal?: unknown },
) => Promise<FetchResponseLike>;

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Send the bearer token (default true). Public routes (`/dev/token`, `/health/live`) pass false. */
  auth?: boolean;
  /** Bearer for this call only (the auth client verifying a freshly minted token). */
  token?: string;
}

const CODES: ReadonlySet<string> = new Set<DataClientErrorCode>([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION',
  'EMAIL_TAKEN',
  'WEAK_PASSWORD',
  'INVALID_CREDENTIALS',
  'RATE_LIMITED',
  'PLAN_LIMIT',
  'GATE_SCORE',
  'GATE_BALANCE',
  'INVALID_STATE',
  'EXPIRED',
  'CANCELLED',
  'ALREADY_JOINED',
  'LINK_ENDED',
  'NOT_IMPLEMENTED',
  'SERVER',
  'NETWORK',
]);

/** Status fallback when the body carries no known code (packages/domain/src/client/errors.ts). */
export function codeForStatus(status: number): DataClientErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'INVALID_STATE';
  if (status === 410) return 'LINK_ENDED';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 402) return 'PLAN_LIMIT';
  if (status === 501) return 'NOT_IMPLEMENTED';
  if (status >= 500) return 'SERVER';
  return 'VALIDATION';
}

export function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, QueryValue>,
): string {
  const base = baseUrl.replace(/\/+$/, '');
  const pairs = Object.entries(query ?? {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return `${base}${path}${pairs.length ? `?${pairs.join('&')}` : ''}`;
}

/**
 * The global fetch, BOUND to the global object. A browser's `fetch` must be called with `this` as the
 * window: stored on an object and called as a method, it throws "Illegal invocation" before any request
 * leaves — which is how the club portal obtained a valid Auth0 token and never reached /v1/org/me.
 * React Native, Node and jsdom do not enforce the receiver, so nothing else could have caught it.
 */
export const defaultFetch = (): FetchLike => {
  const f = (globalThis as { fetch?: unknown }).fetch;
  if (typeof f !== 'function')
    throw new Error('@kk/api-client: no global fetch; pass one explicitly');
  return (f as FetchLike).bind(globalThis);
};

interface AbortControllerLike {
  signal: { aborted: boolean };
  abort(): void;
}
// Timers come from the host (Hermes / Node); the package compiles against the ES lib only.
const timers = globalThis as unknown as {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
};
const newAbortController = (): AbortControllerLike | null => {
  const C = (globalThis as { AbortController?: new () => AbortControllerLike }).AbortController;
  return typeof C === 'function' ? new C() : null;
};

export const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * One JSON request against apps/api. Errors become DataClientError: the body's `error.code` when it is in the
 * DataClient vocabulary, else the status mapping; a failed or timed-out fetch (or a body that cannot be read) is
 * NETWORK; an unparsable 2xx body is SERVER. A call that needs a token and has none throws UNAUTHORIZED without a
 * round trip (the contract's signed-out rule). Requests time out (`timeoutMs`, default 15 s) — React Native on
 * Android would otherwise wait forever on an unreachable LAN address.
 */
export class HttpClient {
  private readonly baseUrl: string;
  private readonly fetch: FetchLike;
  private readonly tokenProvider?: TokenProvider;
  private readonly timeoutMs: number;
  private readonly appVersion?: string;
  private readonly platform?: string;

  constructor(opts: {
    baseUrl: string;
    fetch?: FetchLike;
    tokenProvider?: TokenProvider;
    timeoutMs?: number;
    /**
     * The app's own version, sent as `x-kk-app-version` on EVERY request (build 26). It is how the API tells an app
     * that knows a notification kind from one that crashes on it: a request without the header is 3.0.4 or 3.0.5,
     * and the API holds back what those builds cannot draw (apps/api `heldBackTypes`). Omitted → no header.
     */
    appVersion?: string;
    /**
     * `ios` or `android`, sent as `x-kk-platform` on every request (HQ_INTEGRATION §6, 2026-10-06): what the API
     * records beside a feedback note. Omitted → no header, and the note is stored with no platform.
     */
    platform?: 'ios' | 'android';
  }) {
    if (!opts.baseUrl) throw new Error('@kk/api-client: baseUrl is required (EXPO_PUBLIC_API_URL)');
    this.baseUrl = opts.baseUrl;
    this.fetch = opts.fetch ?? defaultFetch();
    this.tokenProvider = opts.tokenProvider;
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.appVersion = opts.appVersion || undefined;
    this.platform = opts.platform || undefined;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (this.appVersion) headers['x-kk-app-version'] = this.appVersion;
    if (this.platform) headers['x-kk-platform'] = this.platform;
    if (opts.auth !== false) {
      const token = opts.token ?? (await this.tokenProvider?.getAccessToken()) ?? null;
      if (!token) throw new DataClientError('UNAUTHORIZED', 'not signed in');
      headers.authorization = `Bearer ${token}`;
    }
    let body: string | undefined;
    if (opts.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const url = buildUrl(this.baseUrl, path, opts.query);
    const controller = newAbortController();
    const timer = timers.setTimeout(() => controller?.abort(), this.timeoutMs);
    let status: number;
    let text: string;
    try {
      // called detached, never as `this.fetch(…)`: an unbound window.fetch handed in by a caller must
      // not receive the client as its `this` (see defaultFetch)
      const fetchFn = this.fetch;
      const res = await fetchFn(url, {
        method,
        headers,
        body,
        ...(controller ? { signal: controller.signal } : {}),
      });
      status = res.status;
      text = await res.text();
    } catch (e) {
      const timedOut = controller?.signal.aborted === true;
      throw new DataClientError(
        'NETWORK',
        timedOut
          ? `${method} ${path} timed out after ${this.timeoutMs} ms`
          : e instanceof Error
            ? e.message
            : 'network failure',
        e,
      );
    } finally {
      timers.clearTimeout(timer);
    }
    if (status >= 200 && status < 300) {
      if (!text) return undefined as T;
      try {
        return JSON.parse(text) as T;
      } catch (e) {
        throw new DataClientError('SERVER', `malformed response from ${method} ${path}`, e);
      }
    }
    throw toError(status, text, `${method} ${path}`);
  }
}

function toError(status: number, text: string, where: string): DataClientError {
  let parsed: { error?: { code?: unknown; message?: unknown; details?: unknown } } | undefined;
  try {
    parsed = text ? (JSON.parse(text) as typeof parsed) : undefined;
  } catch {
    parsed = undefined;
  }
  const bodyCode = parsed?.error?.code;
  const code =
    typeof bodyCode === 'string' && CODES.has(bodyCode)
      ? (bodyCode as DataClientErrorCode)
      : codeForStatus(status);
  const message =
    typeof parsed?.error?.message === 'string' ? parsed.error.message : `${where} → ${status}`;
  return new DataClientError(code, message, parsed?.error?.details ?? { status });
}
